import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth";
import TrainingBackdrop from "./training/TrainingBackdrop";

const IS_DEV_MOCK = import.meta.env.DEV && import.meta.env.VITE_DEV_MOCK === "true";

export default function LoginView(): React.JSX.Element {
  const { t } = useTranslation();
  const { login } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const urlParams = new URLSearchParams(window.location.search);
  const urlError = urlParams.get("error");

  const handlePasswordLogin = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("auth.invalidCredentials"));
      setLoading(false);
    }
  };

  const handleDevMock = async (): Promise<void> => {
    setLoading(true);
    try {
      await login("admin@tradedesk.local", "admin123");
    } catch {
      // mock mode — just set loading false
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* ── Brand panel — always dark, mirrors the Training academy's trade-route theme ── */}
      <div className="dark hidden lg:flex lg:w-1/2 relative isolate overflow-hidden bg-[#0a1120]">
        <TrainingBackdrop variant="immersive" />
        <div className="relative z-10 flex flex-col justify-between w-full p-10 xl:p-14 select-none cursor-default">
          <span className="text-2xl font-bold tracking-tight text-sky-100">TradeDesk</span>

          <div className="max-w-md space-y-4">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-sky-300">
              {t("auth.eyebrow")}
            </p>
            <h1 className="text-4xl xl:text-5xl font-bold leading-tight text-balance text-white">
              {t("auth.headlineLine1")}
              <br />
              {t("auth.headlineLine2")}
            </h1>
            <div className="h-px w-10 bg-sky-400/60" />
            <p className="text-sm leading-relaxed text-slate-300">{t("auth.tagline")}</p>
          </div>

          <p className="text-xs text-slate-500">{t("auth.copyright", { year: 2026 })}</p>
        </div>
      </div>

      {/* ── Form panel ── */}
      <div className="flex-1 flex items-center justify-center bg-background px-6 py-12">
        <div className="w-full max-w-sm">
          {/* Mobile-only wordmark (brand panel is hidden below lg) */}
          <div className="lg:hidden mb-8 text-center">
            <span className="text-2xl font-bold text-primary">TradeDesk</span>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-foreground text-balance">
              {t("auth.welcomeBack")}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("auth.welcomeSub")}</p>
          </div>

          {(urlError != null || error != null) && (
            <p className="mb-4 text-sm text-destructive text-center bg-destructive/10 rounded-md py-2 px-3">
              {error ?? t("auth.authFailed")}
            </p>
          )}

          {IS_DEV_MOCK ? (
            <button
              type="button"
              onClick={() => void handleDevMock()}
              className="w-full rounded-md bg-amber-500 px-4 py-3 text-sm font-medium text-white hover:bg-amber-600 transition-colors"
            >
              {t("auth.devMock")}
            </button>
          ) : (
            <form onSubmit={(e) => void handlePasswordLogin(e)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t("auth.emailLabel")}
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="you@tradedesk.local"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t("auth.passwordLabel")}
                </label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="••••••••"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-md bg-primary px-4 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                {loading ? t("auth.signingInBtn") : t("auth.signIn")}
              </button>
            </form>
          )}

          <div className="mt-8 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground">TradeDesk</span>
            <div className="h-px flex-1 bg-border" />
          </div>
          <p className="mt-3 text-center text-xs text-muted-foreground">{t("auth.portalTag")}</p>
        </div>
      </div>
    </div>
  );
}
